import { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, Button, Stack } from "@mui/material";

const ClearBoxSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const node = sectionRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.3 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            id="clearbox"
            ref={sectionRef}
            className={`clearbox-hero ${isVisible ? "is-visible" : ""}`}
        >
            <Box
                sx={{
                    maxWidth: "1400px",
                    margin: "0 auto",
                    padding: { xs: "80px 24px 60px", md: "100px 40px 80px" },
                }}
                className="clearbox-hero-inner"
            >
                <Grid
                    container
                    spacing={6}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                        <Box
                            className="clearbox-hero-text"
                            sx={{
                                textAlign: { xs: "center", md: "left" },
                                color: "var(--secondary-color)",
                            }}
                        >
                            {/* Logo / Title */}
                            <Box
                                component="img"
                                src={
                                    process.env.PUBLIC_URL +
                                    `/static/images/ClearBox/clearbox.svg`
                                }
                                alt="ClearBox logo"
                                sx={{
                                    width: { xs: "260px", sm: "320px", md: "380px" },
                                    maxWidth: "100%",
                                    mb: 3,
                                }}
                            />

                            {/* Description */}
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: "1rem", md: "1.1rem" },
                                    maxWidth: { xs: "100%", md: "480px" },
                                    mx: { xs: "auto", md: 0 },
                                    mb: 4,
                                    lineHeight: 1.7,
                                    opacity: 0.95,
                                }}
                            >
                                ClearBox is our advanced monitoring solution designed
                                to bring clarity to complex additive manufacturing processes.
                                Visualize, track, and analyze every build with confidence.
                            </Typography>

                            {/* Actions */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2.5}
                                sx={{
                                    justifyContent: { xs: "center", md: "flex-start" },
                                    alignItems: { xs: "stretch", sm: "center" },
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    href={`${process.env.PUBLIC_URL}/projects/clearbox-buy`}
                                    sx={{
                                        px: 4,
                                        py: 1.2,
                                        borderRadius: "999px",
                                        fontWeight: 700,
                                        textTransform: "none",
                                        backgroundColor: "var(--primary-color)",
                                        boxShadow:
                                            "0 10px 30px rgba(108, 152, 173, 0.6), 0 0 0 1px rgba(255,255,255,0.15)",
                                    }}
                                >
                                    Request a Quote
                                </Button>

                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    href={`${process.env.PUBLIC_URL}/projects/clearbox-working-principle`}
                                    sx={{
                                        px: 4,
                                        py: 1.2,
                                        borderRadius: "999px",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        borderWidth: 2,
                                        borderColor: "rgba(108,152,173,0.7)",
                                        color: "var(--secondary-color)",
                                        "&:hover": {
                                            borderColor: "#6C98AD",
                                            backgroundColor: "rgba(108,152,173,0.06)",
                                        },
                                    }}
                                >
                                    Learn More
                                </Button>
                            </Stack>

                            {/* Lead time note */}
                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 3,
                                    opacity: 0.6,
                                    fontSize: "0.875rem",
                                    fontStyle: "italic",
                                }}
                            >
                                Please note the ClearBox has a lead time of 8 weeks
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Right: ClearBox image */}
                    <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                        <Box
                            className="clearbox-hero-image"
                            sx={{
                                display: "flex",
                                justifyContent: { xs: "center", md: "flex-end" },
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: 320, sm: 420, md: 560 },
                                    height: { xs: 320, sm: 420, md: 560 },
                                    borderRadius: "32px",
                                    border: "1px solid rgba(255,255,255,0.4)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                    position: "relative",
                                }}
                            >
                                <img
                                    src={
                                        process.env.PUBLIC_URL +
                                        "/static/images/ClearBox/clearbox.png"
                                    }
                                    alt="ClearBox"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default ClearBoxSection;

